import type {
  ReplicaAgent,
  ReplicaFile,
  ReplicaPerson,
  ReplicaProject,
  ReplicaState,
  ReplicaThread,
  SceneId
} from "./types.ts";

export const KNOWLEDGE_AGENT_ID = "knowledge";
export const EXCEL_AGENT_ID = "excel";
export const MEETING_AGENT_ID = "meeting";
export const PPT_AGENT_ID = "ppt";
export const CRM_AGENT_ID = "crm";

export function sceneForAgent(id: string): SceneId {
  if (id === KNOWLEDGE_AGENT_ID) return "knowledge-draft";
  if (id === EXCEL_AGENT_ID) return "team-thread";
  if (id === MEETING_AGENT_ID) return "meeting-review";
  if (id === PPT_AGENT_ID || id === CRM_AGENT_ID) return "finalize-thread";
  return "team-thread";
}

export const ZHOU_PERSON_ID = "zhou-yan";
export const CHEN_PERSON_ID = "chen-hang";
export const DIRECT_ZHOU_ID = "direct-zhou";

export const THREAD_MAIN = "chenxing-plan";
export const THREAD_RFP = "chenxing-rfp";
export const THREAD_MEETING = "chenxing-review";
export const PROJECT_CHENXING = "chenxing";
export const MEETING_REVIEW_ID = "internal-review";

export const RFP_FILE: ReplicaFile = {
  name: "晨星汽车园区节能改造-RFP.pdf",
  detail: "客户文件 · 24 页",
  kind: "pdf"
};

export const LEDGER_FILE: ReplicaFile = {
  name: "晨星汽车-2025用能台账.xlsx",
  detail: "客户文件 · 12 个工作表",
  kind: "xlsx"
};

export const DRAFT_V1_FILE: ReplicaFile = {
  name: "晨星汽车-澄清会初稿-v1.md",
  detail: "共同初稿 · 4 条客户引用 · 3 条企业知识引用",
  kind: "markdown",
  provenance: "林深与知识库专家的 Direct Chat"
};

export const WORKBOOK_FILE: ReplicaFile = {
  name: "节能改造ROI测算-v3.xlsx",
  detail: "17.8% 节能率 · 31.4 个月 · 可继续编辑",
  kind: "xlsx",
  provenance: "Excel 专家 · 回收期口径与会前材料"
};

export const COLLAB_V2_FILE: ReplicaFile = {
  name: "晨星汽车-澄清会协作稿-v2.md",
  detail: "团队协作稿 · 待内部预审",
  kind: "markdown",
  provenance: "知识库专家 · 晨星汽车售前协作"
};

export const MEETING_NOTES_FILE: ReplicaFile = {
  name: "晨星汽车-内部预审纪要.md",
  detail: "18 分钟 · 3 项决定 · 带会议时间点",
  kind: "markdown",
  provenance: "会议纪要专家 · 内部方案预审"
};

export const FINAL_V3_FILE: ReplicaFile = {
  name: "晨星汽车-澄清会定稿-v3.md",
  detail: "客户版定稿 · 全部口径已确认",
  kind: "markdown",
  provenance: "知识库专家 · 已确认会议决定"
};

export const PRESENTATION_FILE: ReplicaFile = {
  name: "晨星汽车节能方案-评审版.pptx",
  detail: "8 页 · 企业售前模板 · 可继续编辑",
  kind: "pptx",
  provenance: "PPT 专家 · 定稿 v3 + ROI 测算"
};

export const SOURCE_FILES = [RFP_FILE, LEDGER_FILE] as const;
export const FINAL_FILES = [
  RFP_FILE,
  LEDGER_FILE,
  DRAFT_V1_FILE,
  WORKBOOK_FILE,
  COLLAB_V2_FILE,
  MEETING_NOTES_FILE,
  FINAL_V3_FILE,
  PRESENTATION_FILE
] as const;

const AGENTS: readonly ReplicaAgent[] = [
  {
    id: KNOWLEDGE_AGENT_ID,
    name: "知识库专家",
    blurb: "检索企业知识、保留引用并持续修订初稿",
    seed: "knowledge-expert",
    status: "idle",
    statusLabel: "待命"
  },
  {
    id: EXCEL_AGENT_ID,
    name: "Excel 专家",
    blurb: "核对工作簿、公式测算并交付可编辑结果",
    seed: "excel-expert",
    status: "pending",
    statusLabel: "待邀请"
  },
  {
    id: MEETING_AGENT_ID,
    name: "会议纪要专家",
    blurb: "积累会议历史并提炼带时间点的决定",
    seed: "meeting-notes-expert",
    status: "pending",
    statusLabel: "会议未开始"
  },
  {
    id: PPT_AGENT_ID,
    name: "PPT 专家",
    blurb: "使用企业模板组织可编辑的客户演示",
    seed: "ppt-expert",
    status: "pending",
    statusLabel: "待邀请"
  },
  {
    id: CRM_AGENT_ID,
    name: "CRM 协同专家",
    blurb: "预览并受控写回商机、下一步与附件",
    seed: "crm-collaboration-expert",
    status: "pending",
    statusLabel: "待邀请"
  }
];

const PEOPLE: readonly ReplicaPerson[] = [
  { id: ZHOU_PERSON_ID, name: "周妍", role: "客户经理", seed: "zhou-yan" },
  { id: CHEN_PERSON_ID, name: "陈航", role: "交付工程师", seed: "chen-hang" }
];

const THREADS: readonly ReplicaThread[] = [
  {
    id: THREAD_MAIN,
    title: "回收期口径与会前材料",
    room: "晨星汽车售前协作",
    time: "10:26",
    sceneId: "finalize-thread",
    sender: "林深",
    preview: "按已确认的会议决定更新 v2，保留每项决定对应的会议时间点。",
    replies: 18
  },
  {
    id: THREAD_MEETING,
    title: "内部方案预审 · 3 项决定",
    room: "晨星汽车售前协作",
    time: "10:18",
    sceneId: "meeting-review",
    sender: "会议纪要专家",
    preview: "林深已确认 3 项会议决定，原始时间点随纪要保留。",
    replies: 4
  },
  {
    id: THREAD_RFP,
    title: "RFP 必须响应项与企业案例",
    room: "晨星汽车售前协作",
    time: "09:20",
    sceneId: "team-thread",
    sender: "林深",
    preview: "这是我和知识库专家整理的 v1。周妍确认客户口径，陈航确认切换约束。",
    replies: 7
  }
];

const PROJECTS: readonly ReplicaProject[] = [
  { id: PROJECT_CHENXING, name: "晨星汽车售前协作", tone: "violet" }
];

const ACCOUNT = { name: "林深", initials: "林" } as const;

const RECENT_THREADS = [
  {
    id: THREAD_MAIN,
    title: "回收期口径与会前材料",
    meta: "林深、周妍、陈航 · 18 条回复",
    sceneId: "finalize-thread" as const
  },
  {
    id: THREAD_MEETING,
    title: "内部方案预审 · 3 项决定",
    meta: "会议纪要专家 · 18 分钟",
    sceneId: "meeting-review" as const
  },
  {
    id: THREAD_RFP,
    title: "RFP 必须响应项与企业案例",
    meta: "知识库专家 · 7 条引用",
    sceneId: "team-thread" as const
  }
] as const;

export function baseWorld(sceneId: SceneId): ReplicaState {
  return {
    sceneId,
    view: "thread",
    space: {
      name: "晨星汽车售前协作",
      badge: "项目",
      members: "3 位成员 · 5 个智能体",
      tab: "threads",
      threadTitle: "回收期口径与会前材料",
      threadMeta: "林深、周妍、陈航 · 当前线程",
      composerPlaceholder: "回复当前线程"
    },
    composer: { text: "", typing: false },
    messages: [],
    agents: AGENTS.map((agent) => ({ ...agent })),
    people: PEOPLE.map((person) => ({ ...person })),
    directChats: [
      {
        id: DIRECT_ZHOU_ID,
        personId: ZHOU_PERSON_ID,
        preview: "中午前能给一版可讲的材料吗？",
        time: "09:12",
        unread: sceneId === "intake-dm" ? 1 : 0,
        sceneId: "delivery-dm"
      }
    ],
    threads: THREADS.map((thread) => ({ ...thread })),
    projects: PROJECTS.map((project) => ({ ...project })),
    meeting: {
      id: MEETING_REVIEW_ID,
      title: "晨星汽车内部方案预审",
      status: "scheduled",
      statusLabel: "10:00 开始",
      duration: "00:00",
      syncedSegments: 0,
      candidateDecisions: 0,
      resolvedConflicts: 0,
      actions: 0,
      lines: [],
      summary: null,
      confirmed: false
    },
    hud: {
      summary: "2 个智能体在场",
      fileActivities: [
        {
          id: "rpf-source",
          name: RFP_FILE.name,
          detail: "周妍 · 分享到项目",
          time: "09:20",
          kind: "pdf",
          operation: "uploaded"
        },
        {
          id: "draft-v1",
          name: DRAFT_V1_FILE.name,
          detail: "林深 · 从 Direct Chat 分享",
          time: "09:20",
          kind: "markdown",
          operation: "uploaded"
        }
      ],
      recentThreads: RECENT_THREADS.map((item) => ({ ...item }))
    },
    files: SOURCE_FILES.map((file) => ({ ...file })),
    account: { ...ACCOUNT },
    active: {
      threadId: THREAD_MAIN,
      projectId: PROJECT_CHENXING,
      agentId: null,
      personId: null,
      directChatId: null,
      meetingId: null,
      files: false
    }
  };
}

export function withAgents(
  state: ReplicaState,
  patch: Partial<Record<string, Pick<ReplicaAgent, "status" | "statusLabel">>>
): ReplicaState {
  return {
    ...state,
    agents: state.agents.map((agent) => {
      const next = patch[agent.id];
      return next ? { ...agent, ...next } : agent;
    })
  };
}
